import { updateUserProfileInfo } from '../../ducks/user.duck';
import { updateListingDataOnWishlistAction } from '../../util/api';
import { insertAuthorDataIntoListing } from '../../util/listings';
import { userIsNotAvailable } from '../../util/user';
import {
    getUserWishlistCache,
    removeUserWishlistCache,
    setUserWishlistCache,
} from '../../util/localStorage';

export const DISCARD_WISHLIST_LISTINGS = 'app/FavoritePage/DISCARD_WISHLIST_LISTINGS';
export const SET_WISHLIST_LISTINGS = 'app/FavoritePage/SET_WISHLIST_LISTINGS';
export const SET_WISHLIST_LISTINGS_ERROR = 'app/FavoritePage/SET_WISHLIST_LISTINGS_ERROR';
export const DISCARD_WISHLIST_ERROR = 'app/FavoritePage/DISCARD_WISHLIST_ERROR';
export const SET_WISHLIST_LISTINGS_REQUEST = 'app/FavoritePage/SET_WISHLIST_LISTINGS_REQUEST';
export const CHANGE_WISHLIST_LISTINGS_STATUS = 'app/FavoritePage/CHANGE_WISHLIST_LISTINGS_STATUS';
export const REMOVE_WISHLIST_LISTING = 'app/FavoritePage/REMOVE_WISHLIST_LISTING';
export const ADD_WISHLIST_LISTING = 'app/FavoritePage/ADD_WISHLIST_LISTING';

const initialState = {
    wishlistListings: null,
    wishlistRequestInProgress: false,
    wishlistError: null,
    wishlistListingStatusChanged: false,
    wishlistUnavailableListings: [],
};

const favoritePageReducer = (state = initialState, action = {}) => {
    const { type, payload } = action;

    switch (type) {
        case SET_WISHLIST_LISTINGS_REQUEST:
            return {
                ...state,
                wishlistError: null,
                wishlistRequestInProgress: true,
            };
        case SET_WISHLIST_LISTINGS_ERROR:
            return {
                ...state,
                wishlistError: payload,
                wishlistRequestInProgress: false,
            };
        case DISCARD_WISHLIST_ERROR:
            return {
                ...state,
                wishlistError: null,
            };
        case DISCARD_WISHLIST_LISTINGS:
            return {
                ...state,
                wishlistListings: null,
            };
        case SET_WISHLIST_LISTINGS:
            return {
                ...state,
                wishlistListings: payload,
                wishlistRequestInProgress: false,
                wishlistError: null,
            };
        case CHANGE_WISHLIST_LISTINGS_STATUS:
            return {
                ...state,
                wishlistListingStatusChanged: payload,
            };
        case ADD_WISHLIST_LISTING:
            return {
                ...state,
                wishlistListings: [
                    ...(state.wishlistListings || []).filter(
                        ({ id: { uuid } }) => uuid !== payload.id.uuid
                    ),
                    payload,
                ],
            };
        case REMOVE_WISHLIST_LISTING: {
            const listingsLeft = (state.wishlistListings || []).filter(
                ({ id: { uuid } }) => uuid !== payload
            );
            return {
                ...state,
                /** null, not [], so that the page shows its "no results" state */
                wishlistListings: listingsLeft.length ? listingsLeft : null,
            };
        }
        default:
            return state;
    }
};

export default favoritePageReducer;

export const discardWishlistListings = () => ({
    type: DISCARD_WISHLIST_LISTINGS,
});

export const setWishlistListings = data => ({
    type: SET_WISHLIST_LISTINGS,
    payload: data,
});

export const setWishlistListingsError = error => ({
    type: SET_WISHLIST_LISTINGS_ERROR,
    payload: error,
});

export const discardWishlistError = () => ({
    type: DISCARD_WISHLIST_ERROR,
});

export const setWishlistListingsRequest = () => ({
    type: SET_WISHLIST_LISTINGS_REQUEST,
});

export const changeWishlistListingsStatus = newStatus => ({
    type: CHANGE_WISHLIST_LISTINGS_STATUS,
    payload: newStatus,
});

export const addWishlistListing = listing => ({
    type: ADD_WISHLIST_LISTING,
    payload: listing,
});

export const removeWishlistListing = listingId => ({
    type: REMOVE_WISHLIST_LISTING,
    payload: listingId,
});

/**
 * The local storage cache holds published listings data by listing id
 * (see util/localStorage); it is used only for the user and user type it was written for
 */
const readWishlistCache = ({ userId, userType }) => {
    const cached = getUserWishlistCache();

    const matches = !!cached && cached.userId === userId && cached.userType === userType;

    return matches ? cached : null;
};

const removeFromWishlistCache = ({ userId, userType, removedIds }) => {
    const cached = readWishlistCache({ userId, userType });

    if (!cached) return;

    const listings = { ...cached.listings };
    removedIds.forEach(id => delete listings[id]);

    setUserWishlistCache({ ...cached, listings });
};

const addToWishlistCache = ({ userId, userType, listing }) => {
    const cached = readWishlistCache({ userId, userType });

    if (!cached) return;

    setUserWishlistCache({
        ...cached,
        listings: { ...cached.listings, [listing.id.uuid]: listing },
    });
};

/**
 * Listings which are also in the marketplace store (e.g. fetched by the search or
 * listing page) get their cached attributes refreshed from there. Only attributes are
 * merged: store listings may come with sparse fields, and without the author and
 * images data the cached ones hold. A listing the store knows as no longer published
 * stays in the cache with its new state (it is not shown): it is removed once the
 * server removes it from the user's wishlist and the user closes the alert.
 * @returns the same cached object if nothing matched, an updated copy otherwise
 */
const refreshWishlistCacheFromMarketplaceData = (cached, state) => {
    const listingEntities = state.marketplaceData?.entities?.listing || {};

    const idsInStore = Object.keys(cached.listings).filter(
        id => listingEntities[id] && listingEntities[id].attributes
    );

    if (idsInStore.length === 0) {
        return cached;
    }

    const listings = { ...cached.listings };

    idsInStore.forEach(id => {
        const { attributes } = listingEntities[id];

        listings[id] = {
            ...listings[id],
            attributes: { ...listings[id].attributes, ...attributes },
        };
    });

    return { ...cached, listings };
};

const getCacheKeyData = currentUser => ({
    userId: currentUser.id.uuid,
    userType: currentUser.attributes.profile.publicData.userType,
});
const WISHLIST_LISTING_PARAMS = {
    include: ['author', 'author.profileImage', 'images'],
    'fields.user': [
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.bio',
        'profile.publicData.country',
    ],
    'fields.image': [
        'variants.landscape-crop',
        'variants.landscape-crop2x',
        'variants.square-small',
    ],
};

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** the listing is deleted (404) or its id is malformed (400): it is never going to load */
const isPermanentListingError = error => error && (error.status === 404 || error.status === 400);

const toPublishedListingsById = (listings, included) =>
    listings.reduce((acc, listing) => {
        const listingDataWithAuthor = insertAuthorDataIntoListing(listing, included);

        return listingDataWithAuthor
            ? { ...acc, [listingDataWithAuthor.id.uuid]: listingDataWithAuthor }
            : acc;
    }, {});
/**
 * Fallback for when the batched query fails: each listing is fetched on its own
 * with listings.show (listings.query must not be called per id), so that one failing
 * id does not fail the others. Rejects only if every request fails.
 */
const fetchPublishedListingsOneByOne = async (sdk, ids) => {
    const results = await Promise.allSettled(
        ids.map(id => sdk.listings.show({ id, ...WISHLIST_LISTING_PARAMS }))
    );

    let listingsById = {};
    let loadedCount = 0;
    /** a network or server error: the listing may load next time */
    let temporaryError = null;

    for (const result of results) {
        if (result.status === 'fulfilled') {
            const { data: listing, included } = result.value.data;

            listingsById = { ...listingsById, ...toPublishedListingsById([listing], included) };
            loadedCount += 1;
        } else if (!isPermanentListingError(result.reason)) {
            temporaryError = temporaryError || result.reason;
        }
    }

    if (loadedCount === 0 && temporaryError) {
        throw temporaryError;
    }

    return { listingsById, complete: !temporaryError };
};
/**
 * One listings.query for all the ids; if it fails, the listings are fetched one by one.
 * @returns {Promise<{ listingsById: Object<string, Object>, complete: boolean }>}
 * published listings by listing id - closed, deleted etc. listings are left out;
 * complete is false if some listings failed to load for a temporary reason
 */
const fetchPublishedListingsById = (sdk, ids) => {
    const validIds = ids.filter(id => UUID_REGEX.test(id));

    if (validIds.length === 0) {
        return Promise.resolve({ listingsById: {}, complete: true });
    }

    return sdk.listings
        .query({ ids: validIds, ...WISHLIST_LISTING_PARAMS })
        .then(({ data: { data: listings, included } }) => ({
            listingsById: toPublishedListingsById(listings, included),
            complete: true,
        }))
        .catch(() => fetchPublishedListingsOneByOne(sdk, validIds));
};
/**
 * If there is a wishlist cache, it is used as it is; otherwise the listings are fetched
 * and cached. The cache is then kept in sync by adding and removing wishlist items.
 */
const showWishlistListings = (ids, listingsById) => dispatch => {
    const listings = ids
        .map(id => listingsById[id])
        .filter(listing => listing && listing.attributes.state === 'published');

    dispatch(setWishlistListings(listings));
};
/**
 * Ids of listings which are cached, but no longer in the user's wishlist:
 * the server removes a closed or deleted listing from the wishlists of the users
 * who favoured it (see IListener.notifyRidersRelatedToListing)
 */
const getUnavailableCachedIds = (ids, cached) =>
    Object.keys(cached.listings).filter(id => !ids.includes(id));

const showCachedWishlistListings = (ids, cached) => (dispatch, getState) => {
    const refreshed = refreshWishlistCacheFromMarketplaceData(cached, getState());

    if (refreshed !== cached) {
        setUserWishlistCache(refreshed);
    }

    dispatch(showWishlistListings(ids, refreshed.listings));
};

const getWishlistIds = currentUser => {
    const { userType } = currentUser.attributes.profile.publicData;
    const { wishlist } = currentUser.attributes.profile.protectedData || {};
    const ids = wishlist && wishlist[userType];

    return Array.isArray(ids) && ids.length > 0 ? ids : null;
};

export const queryWishlistListings = ids => async (dispatch, getState, sdk) => {
    const cacheKeyData = getCacheKeyData(getState().user.currentUser);
    const cached = readWishlistCache(cacheKeyData);

    if (cached) {
        dispatch(showCachedWishlistListings(ids, cached));
        /**
         * drives the notification dot in the topbar menu
         * and the alert on the favourites page
         */
        dispatch(changeWishlistListingsStatus(getUnavailableCachedIds(ids, cached).length > 0));
        return;
    }

    dispatch(discardWishlistListings());
    dispatch(setWishlistListingsRequest());
    dispatch(changeWishlistListingsStatus(false));

    return fetchPublishedListingsById(sdk, ids)
        .then(({ listingsById, complete }) => {
            dispatch(showWishlistListings(ids, listingsById));
            /** a cache missing some listings would never load them, so the next load retries */
            if (complete) {
                setUserWishlistCache({ ...cacheKeyData, listings: listingsById });
            }
        })
        .catch(e => dispatch(setWishlistListingsError(true)));
};
/**
 * Adds a newly favoured listing to the cache and to the list;
 * without a cache, the whole wishlist is fetched, which creates one
 */
const addListingToWishlist = listingId => (dispatch, getState, sdk) => {
    const cacheKeyData = getCacheKeyData(getState().user.currentUser);

    if (!readWishlistCache(cacheKeyData)) {
        return dispatch(getUserWishlist());
    }

    return fetchPublishedListingsById(sdk, [listingId])
        .then(({ listingsById }) => {
            const listing = listingsById[listingId];

            if (!listing) return;

            addToWishlistCache({ ...cacheKeyData, listing });
            dispatch(addWishlistListing(listing));
        })
        .catch(e => {
            /** the cache would miss this listing, so let the next load rebuild it */
            removeUserWishlistCache();
        });
};

/**
 * Called when the alert on the favourites page is closed: removes the listings
 * which are no longer in the user's wishlist from the cache and from the list.
 * The user data is not touched, the server has already updated it.
 */
export const removeUnavailableListings = () => (dispatch, getState) => {
    const {
        user: { currentUser },
    } = getState();
    if (userIsNotAvailable(currentUser)) return;

    const cacheKeyData = getCacheKeyData(currentUser);
    const ids = getWishlistIds(currentUser) || [];
    const cached = readWishlistCache(cacheKeyData);

    if (cached) {
        removeFromWishlistCache({
            ...cacheKeyData,
            removedIds: getUnavailableCachedIds(ids, cached),
        });
    }

    (getState().FavoritePage.wishlistListings || [])
        .filter(({ id: { uuid } }) => !ids.includes(uuid))
        .forEach(({ id: { uuid } }) => dispatch(removeWishlistListing(uuid)));

    dispatch(changeWishlistListingsStatus(false));
};

export const getUserWishlist = () => (dispatch, getState, sdk) => {
    const {
        user: { currentUser },
    } = getState();
    if (userIsNotAvailable(currentUser)) return;
    /**
     * wishlist
     * [userType]: [id1, id2]
     */
    const ids = getWishlistIds(currentUser);

    if (!ids) return;

    dispatch(queryWishlistListings(ids));
};
/**
 * Called when the favourites page is opened: refreshes the cached listings with
 * the ones fetched meanwhile by other pages (e.g. search), with no request.
 * Without a cache it does nothing: the initial fetch of the current user
 * middleware creates it.
 */
export const refreshWishlistListings = () => (dispatch, getState) => {
    const {
        user: { currentUser },
    } = getState();
    if (userIsNotAvailable(currentUser)) return;

    const ids = getWishlistIds(currentUser);
    const cached = ids && readWishlistCache(getCacheKeyData(currentUser));

    if (!cached) return;

    dispatch(showCachedWishlistListings(ids, cached));
};

const extractWishlistParamsUpdated = (currentUser, listingId, action) => {
    try {
        const {
            protectedData: { wishlist: existingWishlist },
            publicData: { userType },
        } = currentUser.attributes.profile;

        const wishlistDataExists =
            existingWishlist &&
            existingWishlist[userType] &&
            Array.isArray(existingWishlist[userType]);

        const extractUserWishlistData = () => {
            if (action === 'add') {
                return wishlistDataExists
                    ? [...existingWishlist[userType], listingId]
                    : [listingId];
            }

            if (action === 'remove') {
                return wishlistDataExists
                    ? existingWishlist[userType].filter(id => id !== listingId)
                    : [];
            }
        };

        return {
            protectedData: {
                wishlist: {
                    ...(existingWishlist || {}),
                    [userType]: extractUserWishlistData(),
                },
            },
        };
    } catch (e) {
        return null;
    }
};

export const addWishlistItem = listingId => (dispatch, getState, sdk) => {
    const action = 'add';

    const {
        user: { currentUser },
    } = getState();

    const params = extractWishlistParamsUpdated(currentUser, listingId, action);

    if (!params) {
        return dispatch(setWishlistListingsError(true));
    }

    const currentUserDataUpd = { ...currentUser };
    currentUserDataUpd.attributes.profile.protectedData.wishlist = {
        ...params.protectedData.wishlist,
    };

    return dispatch(updateUserProfileInfo(params, currentUserDataUpd))
        .then(() => {
            dispatch(addListingToWishlist(listingId));
            return updateListingDataOnWishlistAction(listingId, currentUser.id.uuid, 'add');
        })
        .catch(e => dispatch(setWishlistListingsError(true)));
};

export const deleteWishlistItem = listingId => (dispatch, getState, sdk) => {
    const action = 'remove';

    const {
        user: { currentUser },
    } = getState();

    const params = extractWishlistParamsUpdated(currentUser, listingId, action);

    if (!params) {
        return dispatch(setWishlistListingsError(true));
    }

    const currentUserDataUpd = { ...currentUser };
    currentUserDataUpd.attributes.profile.protectedData.wishlist = {
        ...params.protectedData.wishlist,
    };

    return dispatch(updateUserProfileInfo(params, currentUserDataUpd))
        .then(() => updateListingDataOnWishlistAction(listingId, currentUser.id.uuid, action))
        .then(() => {
            dispatch(removeWishlistListing(listingId));
            removeFromWishlistCache({
                userId: currentUser.id.uuid,
                userType: currentUser.attributes.profile.publicData.userType,
                removedIds: [listingId],
            });
        })
        .catch(e => dispatch(setWishlistListingsError(true)));
};
